import { Box } from '@mui/system';
import { useFetchAgaveCommonData } from 'app/hooks/useFetchAgaveCommonData';
import { useFetchProcoreCommonData } from 'app/hooks/useFetchProcoreCommonData';
import { useErpSourceSystem } from 'app/hooks/useErpSourceSystem';
import { useControlCenterEntrySnackbar } from 'app/hooks/useControlCenterEntrySnackbar';
import AppHeader from 'app/shared-components/layout/AppHeader';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import { paidolService } from 'app/services/paidol.service';
import { userService } from 'app/services/user.service';
import { highnoteService } from 'app/services/highnote.service';
import { agaveService } from 'app/services/agave.service';
import { reimbursementsService } from 'app/services/reimbursements.service';
import { cardService } from 'app/services/card.service';
import { transactionService } from 'app/services/transaction.service';
import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import NavBar from './navbar/NavBar';

function Layout(): JSX.Element {
  const [searchParams] = useSearchParams();
  const codeParams = searchParams.getAll('procore_code');
  const codeParam = codeParams.length > 1 ? codeParams[1] : codeParams[0]; // Take the latest code

  const location = useLocation();
  const isReviewPage = location.pathname.match(/^\/review/);
  const isMobileDemoPage = location.pathname === '/mobile-demo';

  useFetchProcoreCommonData(codeParam);
  useFetchAgaveCommonData();
  // Lazy-loads the active ERPConnection record into Redux for ErpConnection
  // paidols (no-op for paidols without erpConnectionId). Call site is here
  // to mirror the useFetchAgaveCommonData mount pattern.
  useErpSourceSystem();
  // SEE-265: "switched to Control Center" snackbar. Lives here because Layout stays
  // mounted across navigation, so it sees the path move into /control-center.
  useControlCenterEntrySnackbar();

  const warmUp = async () => {
    await Promise.all([
      cardService.v2CardHealthCheck(new Date().toISOString()),
      transactionService.v2TransactionHealthCheck(new Date().toISOString()),
      userService.v2UserHealthCheck(new Date().toISOString()),
      paidolService.v2PaidolHealthCheck(new Date().toISOString()),
      highnoteService.v2HighnoteHealthCheck(new Date().toISOString()),
      agaveService.v2AgaveResolverHealthCheck(new Date().toISOString()),
      agaveService.v2AgaveHealthCheck(new Date().toISOString()),
      reimbursementsService.v2ReimbursementsHealthCheck(new Date().toISOString()),
    ]);
  };

  useEffect(() => {
    warmUp();
  }, []);

  return (
    <>
      <AppHeader />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        {!isReviewPage && !isMobileDemoPage && <NavBar />}
        <Suspense fallback={<LoadingOverlay isLoading={true} blockInteraction={true} />}>
          <Outlet />
        </Suspense>
      </Box>
    </>
  );
}

export default Layout;
